import React from 'react';
import { FormHelpers, TaskHelpers } from 'ontraccr-common';
import { DateTime } from 'luxon';
import { LUXON_DATE_FORMAT } from "../../forms/FormBuilder/FormFields/TimeEntryTable/TimeEntryTable.constants";
import { sortByObjProperty } from '../../common/helpers';

export default {};

const doesValueMatchId = ({
  value,
  id,
}) => {
  if (!value) return false;
  return value.bucketTemplateId === id;
};

const getDownstreamBuckets = ({
  bucket,
  property,
  bucketTemplateId,
  secondaryBucketMap,
}) => {
  const formattedBucket = { ...bucket };
  formattedBucket[property] = [];

  formattedBucket.downstreamTypes?.forEach((type) => {
    if (type.split('-')?.[0] !== bucketTemplateId) return;
    const relevantDownstreamBuckets = formattedBucket[type];
    if (!relevantDownstreamBuckets?.length) return;
    formattedBucket[property].push(
      ...relevantDownstreamBuckets
        .map((downstreamBucket) => secondaryBucketMap[downstreamBucket])
        .filter(Boolean),
    );
  });

  return formattedBucket;
};

export const getStatus = (record, statuses) => {
  if (!record) return '';
  const relevantTemplateStatuses = statuses[record.bucketTemplateId];
  return relevantTemplateStatuses?.[record.statusId]?.status;
};

// Flattens a bucket's customData fields onto top-level keys (keyed by fieldId)
// so the latest bucket values are read instead of the stale snapshot captured
// in the form data at submission time.
const flattenBucketCustomData = (bucket) => {
  if (!bucket?.customData?.fields?.length) return bucket;
  const flattened = { ...bucket };
  bucket.customData.fields.forEach((field) => {
    flattened[field.fieldId] = TaskHelpers.formatCustomDataForTimeTable({
      response: field.response,
      type: field.type,
      title: field.title,
    });
  });
  return flattened;
};

const linkBuckets = ({
  items,
  segments,
  bidItems,
  bucketMap,
  segmentId,
  bidItemId,
  bidItemMap,
  statuses,
}) => {
  const segmentMap = {};
  const formattedSegments = segments.map((segment) => {
    const relevantId = segment.bucketId || segment.id;
    const relevantBucket = bucketMap[relevantId];
    if (!relevantBucket) {
      if (getStatus(segment, statuses) === 'Historical') return null;
      segmentMap[relevantId] = segment;
      return segment;
    }

    const formattedSegment = getDownstreamBuckets({
      bucket: { ...segment, ...flattenBucketCustomData(relevantBucket) },
      property: 'bidItems',
      bucketTemplateId: bidItemId,
      bucketMap,
      secondaryBucketMap: bidItemMap,
    });
    const nonHistoricalBidItems = formattedSegment.bidItems?.filter((bidItem) => getStatus(bidItem, statuses) !== 'Historical') || [];
    formattedSegment.bidItems = nonHistoricalBidItems;
    formattedSegment.numberOfBidItems = nonHistoricalBidItems.length || 0;

    if (getStatus(formattedSegment, statuses) === 'Historical') return null;

    segmentMap[relevantId] = formattedSegment;
    return formattedSegment;
  }).filter(Boolean);

  const formattedItems = items.map((item) => {
    const relevantId = item.bucketId || item.id;
    const relevantBucket = bucketMap[relevantId];
    if (!relevantBucket) return item;

    const formattedItem = getDownstreamBuckets({
      bucket: { ...item, ...flattenBucketCustomData(relevantBucket) },
      property: 'segments',
      bucketTemplateId: segmentId,
      bucketMap,
      secondaryBucketMap: segmentMap,
    });
    const itemBidItems = [];
    formattedItem.segments = formattedItem.segments?.map((segment) => {
      const segmentBidItems = [
        ...(
          segment?.bidItems?.map((bidItem) => ({
            ...bidItem,
            parentItem: formattedItem,
            parentSegment: segment,
            segmentId: segment.id,
          }))
        ) || []
      ];
      itemBidItems.push(...segmentBidItems);
      return {
        ...segment,
        bidItems: segmentBidItems,
      };
    });
    formattedItem.bidItems = itemBidItems;
    formattedItem.numberOfSegments = formattedItem.segments?.length || 0;
    formattedItem.numberOfBidItems = formattedItem.segments?.reduce((acc, segment) => (
      acc + (segment.numberOfBidItems || 0)
    ), 0);
    return formattedItem;
  });

  formattedItems.sort(sortByObjProperty('name'));

  return {
    items: formattedItems,
    segments: formattedSegments,
    bidItems,
  };
};

export const getFormattedValue = ({
  item,
  statuses,
  dataIndex,
}) => {
  if (!item) return '';
  if (dataIndex !== 'statusId') return item[dataIndex];

  const relevantStatuses = statuses[item.bucketTemplateId];
  return relevantStatuses?.[item.statusId]?.status || '';
};

export const getABWorkReportingData = ({
  data = [],
  itemId,
  segmentId,
  bidItemId,
  bucketMap = {},
  statuses = {},
  abWorkReportingEntryMap = {},
}) => {
  const items = [];
  const segments = [];
  const bidItems = [];
  const itemMap = {};
  const segmentMap = {};
  const bidItemMap = {};

  if (!data) return { items, segments, bidItems };
  data.forEach(({ fields = [] }) => {
    fields.forEach(({ type, response }) => {
      if ((type !== 'table' && type !== 'dropdown') || !response) return;
      const { values = [] } = response;
      values.forEach((value) => {
        if (doesValueMatchId({ value, id: itemId })) {
          items.push(value);
          itemMap[value.bucketId || value.id] = value;
          return;
        }
        if (doesValueMatchId({ value, id: segmentId })) {
          segments.push(value);
          segmentMap[value.bucketId || value.id] = value;
          return;
        }
        if (doesValueMatchId({ value, id: bidItemId })) {
          const relevantBucket = bucketMap[value.bucketId || value.id] || {};
          relevantBucket.customData?.fields?.forEach((field) => {
            relevantBucket[field.fieldId] = TaskHelpers.formatCustomDataForTimeTable({
              response: field.response,
              type: field.type,
              title: field.title,
            });
          });
          const formattedValue = {
            ...value,
            ...relevantBucket,
            repQty: abWorkReportingEntryMap[value.bucketId || value.id] || 0,
          };
          bidItems.push(formattedValue);
          bidItemMap[value.bucketId || value.id] = formattedValue;
        }
      });
    });
  });

  return linkBuckets({
    items,
    segments,
    bidItems,
    bucketMap,
    segmentId,
    bidItemId,
    bidItemMap,
    statuses,
  });
};

export const workReportEntryDisplayFieldOptions = [
  {
    label: 'AT WO#',
    value: 'cardName',
  },
  {
    label: 'Accomplishment',
    value: 'quantity',
  },
  {
    label: 'Date Worked',
    value: 'dateWorked',
  },
  {
    label: 'Comments',
    value: 'comments',
  },
  {
    label: 'Status',
    value: 'reportStatus',
  },
  {
    label: 'Posted Date',
    value: 'createdAt',
  },
  {
    label: 'Work Number',
    value: 'workNumber',
  },
  {
    label: 'Batch Number',
    value: 'batchNumber',
  },
  {
    label: 'Worksheet Number',
    value: 'worksheetNumber',
  },
  {
    label: 'Contract',
    value: 'contract',
  },
  {
    label: 'Task Number',
    value: 'taskNumber',
  },
  {
    label: 'Work Order Number',
    value: 'workOrderNumber',
  },
];

export const combineBucketTemplates = ({
  itemBucket,
  segmentBucket,
  bidItemBucket,
}) => {
  const combinedBucket = {
    customFields: {
      fields: [],
    },
  };
  if (itemBucket) combinedBucket.customFields.fields.push(...itemBucket.customFields?.fields || []);
  if (segmentBucket) combinedBucket.customFields.fields.push(...segmentBucket.customFields?.fields || []);
  if (bidItemBucket) combinedBucket.customFields.fields.push(...bidItemBucket.customFields?.fields || []);
  return combinedBucket;
};

const extractBucketData = (bucket, prefix) => {
  if (!bucket) return { bucket: {}, customData: {} };

  const baseBucket = { ...bucket };
  const customDataMap = {};
  Object.keys(baseBucket).forEach((key) => {
    baseBucket[`${prefix}.${key}`] = baseBucket[key];
    customDataMap[`${prefix}-${key}`] = { value: baseBucket[key] };
  });
  baseBucket.customData?.fields?.forEach((field) => {
    const formattedValue = TaskHelpers.formatCustomDataForTimeTable({
      response: field.response,
      type: field.type,
      title: field.title,
    });
    baseBucket[field.fieldId] = formattedValue;
    baseBucket[`${prefix}.${field.fieldId}`] = formattedValue;
    customDataMap[field.fieldId] = field.response;
  });
  return {
    bucket: baseBucket,
    customData: customDataMap,
  };
};

const additionalColumnMap = {
  workOrderNumber: {
    title: 'Work Order Number',
    dataIndex: 'cardName',
    type: 'text',
  },
  cardName: {
    title: 'AT WO#',
    dataIndex: 'cardName',
    type: 'text',
  },
  quantity: {
    title: 'Accomplishment',
    dataIndex: 'quantity',
    type: 'calculation',
  },
  dateWorked: {
    title: 'Date Worked',
    dataIndex: 'dateWorked',
    width: 100,
    type: 'dateTime',
    dateOnly: true,
    render: (dateWorked) => (
      dateWorked
        ? DateTime.fromFormat(dateWorked, 'yyyyMMdd').toFormat('M/d/yyyy')
        : ''
    ),
  },
  comments: {
    title: 'Comments',
    dataIndex: 'comments',
    type: 'text',
  },
  reportStatus: {
    title: 'Status',
    dataIndex: 'status',
    type: 'dropdown',
    dropdownOptions: [
      {
        id: 'Active',
        name: 'Active',
      },
      {
        id: 'Exported',
        name: 'Exported',
      },
      {
        id: 'Voided',
        name: 'Voided',
      },
      {
        id: 'Pending',
        name: 'Pending',
      },
    ]
  },
  createdAt: {
    title: 'Posted Date',
    dataIndex: 'createdAt',
    width: 100,
    render: (createdAt) => (
      createdAt
        ? DateTime.fromMillis(createdAt).toFormat(LUXON_DATE_FORMAT)
        : ''
    ),
    type: 'dateTime',
  },
  workNumber: {
    title: 'Work Number',
    dataIndex: 'workNumber',
    width: 100,
    type: 'text',
    doNotCalculate: true,
  },
  batchNumber: {
    title: 'Batch Number',
    dataIndex: 'batchNumber',
    width: 100,
    type: 'calculation',
    doNotCalculate: true,
  },
  worksheetNumber: {
    title: 'Worksheet Number',
    dataIndex: 'worksheetNumber',
    width: 100,
    type: 'calculation',
    doNotCalculate: true,
  },
  taskNumber: {
    title: 'Task Number',
    dataIndex: 'number',
    width: 100,
    type: 'calculation',
    doNotCalculate: true,
  },
};

const BUCKET_PREFIXES = ['item', 'segment', 'bidItem'];

const validateData = (data, filters) => {
  const dataToCheck = { ...data };
  const processedDataIndexes = new Set();
  Object.keys(additionalColumnMap).forEach((key) => {
    const {
      type,
      dataIndex,
    } = additionalColumnMap[key];
    if (processedDataIndexes.has(dataIndex)) return;
    processedDataIndexes.add(dataIndex);
    switch (type) {
      case 'dateTime':
        dataToCheck[dataIndex] = { date: dataToCheck[dataIndex] };
        break;
      case 'dropdown': {
        const values = [];
        if (dataToCheck[dataIndex]) values.push({ id: dataToCheck[dataIndex] });
        dataToCheck[dataIndex] = { values };
        break;
      }
      default:
        dataToCheck[dataIndex] = { value: dataToCheck[dataIndex] };
        break;
    }
  });

  // The formula evaluator splits dot-notation fields like 'bidItem.field-xxx'
  // into fieldId='bidItem' and secondaryId='field-xxx', then does a table row lookup:
  // responses['bidItem'].values.some((val) => val['field-xxx'] matches).
  // Build that structure here from the prefixed keys already in dataToCheck.
  BUCKET_PREFIXES.forEach((prefix) => {
    const prefixDot = `${prefix}.`;
    const entry = {};
    Object.keys(dataToCheck).forEach((key) => {
      if (!key.startsWith(prefixDot)) return;
      const subKey = key.slice(prefixDot.length);
      const rawValue = dataToCheck[key];
      entry[subKey] = rawValue?.value !== undefined ? rawValue.value : rawValue;
    });
    if (Object.keys(entry).length) {
      dataToCheck[prefix] = { values: [entry] };
    }
  });

  return FormHelpers.executeConditionalRenderingFormula({
    formula: filters,
    responses: dataToCheck,
  });
};

const getDownstreamIds = (bucketMap, id, downstreamType) => {
  const bucket = bucketMap[id];
  if (!bucket?.downstreamTypes?.length) return [];
  const matchingDownstreamKey = bucket.downstreamTypes.find(
    (type) => type.split('-')?.[0] === downstreamType,
  );
  if (!matchingDownstreamKey) return [];
  return bucket[matchingDownstreamKey] || [];
}

/**
 * Get the relevant segment options for the item
 * @param {string} itemBucketId - Item id of the entry
 * @param {string} segmentBucketTemplateId - segment template id from AB work reporting mapping
 * @param {Object<string, object>} bucketMap - map of buckets
 * @param {string} bidItemName - name of the bid item
 */
export const getSegmentOptionsForABWorkReportEdit = ({
  itemId,
  segmentBucketTemplateId,
  bidItemBucketTemplateId,
  bucketMap = {},
  bidItemName,
  statuses = {},
}) => {
  if (!itemId || !segmentBucketTemplateId || !bucketMap) return [];
  const segmentIds = getDownstreamIds(bucketMap, itemId, segmentBucketTemplateId);
  return segmentIds.map((segmentId) => {
    const segment = bucketMap[segmentId];
    if (!segment || getStatus(segment, statuses) === 'Historical') return null;
    const bidItemIds = getDownstreamIds(bucketMap, segmentId, bidItemBucketTemplateId);
    const relevantBidItem = bidItemIds.find((bidItemId) => {
      const bidItem = bucketMap[bidItemId];
      return bidItem?.name === bidItemName && getStatus(bidItem, statuses) !== 'Historical';
    });
    if (!relevantBidItem) return null;
    return { value: segmentId, label: segment.name, bidItemId: relevantBidItem };
  }).filter(Boolean);
};

/**
 * Get the bid item options for a given segment.
 * Returns every bid item bucket downstream of the segment so the user can
 * swap an entry to any bid item under that segment (not just the one that
 * shares the entry's current bid item name).
 */
export const getBidItemOptionsForABWorkReportEdit = ({
  segmentId,
  bidItemBucketTemplateId,
  bucketMap = {},
  statuses = {},
}) => {
  if (!segmentId || !bidItemBucketTemplateId || !bucketMap) return [];
  const bidItemIds = getDownstreamIds(bucketMap, segmentId, bidItemBucketTemplateId);
  return bidItemIds
    .map((bidItemId) => {
      const bidItem = bucketMap[bidItemId];
      if (!bidItem || getStatus(bidItem, statuses) === 'Historical') return null;
      return {
        value: bidItemId,
        label: bidItem.name,
        segmentId,
      };
    })
    .filter(Boolean);
};

export const getWorkReportEntryData = ({
  workReportEntries = [],
  bucketMap = {},
  filters = [],
}) => {
  const data = [];
  const isFormulaComplete = FormHelpers.isConditionalRenderingFormulaComplete(filters);

  workReportEntries.forEach((workReportEntry) => {
    const relevantItem = bucketMap[workReportEntry.itemId];
    const relevantSegment = bucketMap[workReportEntry.segmentId];
    const relevantBidItem = bucketMap[workReportEntry.bidItemId];

    const { bucket: relevantItemBucket, customData: relevantItemCustomData } = extractBucketData(relevantItem, 'item');
    const { bucket: relevantSegmentBucket, customData: relevantSegmentCustomData } = extractBucketData(relevantSegment, 'segment');
    const { bucket: relevantBidItemBucket, customData: relevantBidItemCustomData } = extractBucketData(relevantBidItem, 'bidItem');

    const formattedData = {
      ...workReportEntry,
      ...relevantItemBucket,
      ...relevantSegmentBucket,
      ...relevantBidItemBucket,
      id: workReportEntry.id,
    };

    if (workReportEntry.dateWorked) {
      formattedData.dateWorked = DateTime.fromISO(workReportEntry.dateWorked).toFormat('yyyyMMdd');
    }

    if (!isFormulaComplete) {
      data.push(formattedData);
      return;
    }

    // Add custom data to the data to check
    const dataToCheck = {
      ...formattedData,
      ...relevantItemCustomData,
      ...relevantSegmentCustomData,
      ...relevantBidItemCustomData,
      id: workReportEntry.id,
    };

    if (validateData(dataToCheck, filters)) data.push(formattedData);
  });

  return data;
};

const bucketTypes = [
  {
    value: 'item',
    title: 'Item',
  },
  {
    value: 'segment',
    title: 'Segment',
  },
  {
    value: 'bidItem',
    title: 'Bid Item',
  }
];

export const getAdditionalColumnMap = (relevantStatuses, contractField) => {
  const newMap = { ...additionalColumnMap };

  if (contractField) {
    newMap.contract = {
      title: 'Contract',
      dataIndex: contractField,
      type: 'text',
      doNotCalculate: true,
    };
  }

  bucketTypes.forEach((bucketType) => {
    newMap[`${bucketType.value}-name`] = {
      title: `${bucketType.title} Name`,
      dataIndex: `${bucketType.value}-name`,
      type: 'text',
    };
    newMap[`${bucketType.value}-description`] = {
      title: `${bucketType.title} Description`,
      dataIndex: `${bucketType.value}-description`,
      type: 'text',
    };
    newMap[`${bucketType.value}-status`] = {
      title: `${bucketType.title} Status`,
      dataIndex: `${bucketType.value}-statusId`,
      render: (statusId) => relevantStatuses[statusId]?.status || '',
    };
  });

  return newMap;
};

export const convertTableColumnsToArray = (tableColumns, settingColumns) => {
  // If no particular order then use default order
  if (!settingColumns?.length) {
    return Object.keys(tableColumns).map((key) => ({
      key,
      name: tableColumns[key],
    }));
  }

  return settingColumns
    .filter((column) => !!tableColumns[column])
    .map((column) => ({
      key: column,
      name: tableColumns[column],
    }));
};

export const isClosedOrHistoricalBucket = (record, statuses) => {
  const label = getStatus(record, statuses);
  return label === 'Closed' || label === 'Historical';
};

/**
 * Status dropdown options for AB Work Reporting: never offer Historical as a choice.
 * If the current status is Historical, keep that option so a disabled Select can show the label.
 */
export const getABWorkStatusSelectOptions = ({ relevantStatuses = {}, currentStatusId }) => {
  const all = Object.values(relevantStatuses);
  const withoutHistorical = all.filter((s) => s.status !== 'Historical');
  const current = all.find((s) => s.id === currentStatusId);
  if (current?.status === 'Historical') {
    return [...withoutHistorical, current];
  }
  return withoutHistorical;
};

export const getFiscalPeriod = (date, format = 'M/d/yyyy ') => {
  if (!date) return null;
  const parsedDateTime = DateTime.fromFormat(date, format);
  if (!parsedDateTime.isValid) return null;
  if (parsedDateTime.month < 4) return `${parsedDateTime.year - 1}/${parsedDateTime.year}`;
  return `${parsedDateTime.year}/${parsedDateTime.year + 1}`;
}

/**
 * Validates the work report entries, in order for entries to be exported:
 * - There must be at least one entry
 * - All entries must fall under the same fiscal period
 * For alberta the fiscal period is from April 1st - March 31st so we will hardcode this for now
 * - All entries must have the same contract
 * If any of these conditions are not met
 * We will return an error message containing the reason why the entries are not valid
 */
export const validateWorkReportEntries = ({
  workReportEntries = [],
  dateField,
  contractField,
}) => {
  if (!workReportEntries?.length) {
    return 'There must be at least one entry';
  }

  const contracts = new Set();
  const fiscalPeriods = new Set();
  const contractMap = {};
  const fiscalPeriodMap = {};

  const noContractError = [];
  const noFiscalPeriodError = [];
  const invalidFiscalPeriodError = [];

  workReportEntries.forEach((workReportEntry) => {
    const contract = workReportEntry[contractField];
    const fiscalPeriod = workReportEntry[dateField];

    if (!contract) noContractError.push(workReportEntry.id);
    if (!fiscalPeriod) noFiscalPeriodError.push(workReportEntry.id);
    if (!contract || !fiscalPeriod) return;

    contracts.add(contract);
    contractMap[contract] = contractMap[contract] || [];
    contractMap[contract].push(workReportEntry.id);

    const parsedFiscalPeriod = getFiscalPeriod(fiscalPeriod);
    if (!parsedFiscalPeriod) {
      invalidFiscalPeriodError.push(workReportEntry.id);
      return;
    }

    fiscalPeriods.add(parsedFiscalPeriod);
    fiscalPeriodMap[parsedFiscalPeriod] = fiscalPeriodMap[parsedFiscalPeriod] || [];
    fiscalPeriodMap[parsedFiscalPeriod].push(workReportEntry.id);
  });

  if (
    !noContractError.length
    && !noFiscalPeriodError.length
    && !invalidFiscalPeriodError.length
    && contracts.size === 1
    && fiscalPeriods.size === 1
  ) {
    return null;
  }

  return (
    <div>
      {!!noContractError.length && (
        <div>{noContractError.length} entries were found with no contract.</div>
      )}
      {!!noFiscalPeriodError.length && (
        <div>{noFiscalPeriodError.length} entries were found with no fiscal period.</div>
      )}
      {!!invalidFiscalPeriodError.length && (
        <div>{invalidFiscalPeriodError.length} entries were found with an invalid fiscal period.</div>
      )}
      {contracts.size > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div>Multiple contracts were found:</div>
          <ul style={{ textAlign: 'left' }}>
            {Array.from(contracts).map((contract) => (
              <li key={contract}>{contract}: {contractMap[contract]?.length} entries</li>
            ))}
          </ul>
        </div>
      )}
      {fiscalPeriods.size > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div>Multiple fiscal periods were found:</div>
          <ul style={{ textAlign: 'left' }}>
            {Array.from(fiscalPeriods).map((fiscalPeriod) => (
              <li key={fiscalPeriod}>{fiscalPeriod}: {fiscalPeriodMap[fiscalPeriod]?.length} entries</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Sorting entries for export
 * 1. Work Date - dateWorked prop
 * 2. Work Order Number - cardName prop
 * 3. Work Order Line Item Number - item-name prop
 * 4. Activity / Bid Item Number - bidItem-name prop
 * 5. Segment ID - segment-name prop
 */
const sortEntriesForExport = (workReportEntries) => {
  // Sort in order of those properties above
  return [...workReportEntries].sort((a, b) => {
    const dateA = DateTime.fromFormat(a.dateWorked, 'yyyyMMdd')
    const dateB = DateTime.fromFormat(b.dateWorked, 'yyyyMMdd');
    if (!dateA.isValid || !dateB.isValid) return 0;
    const dateDiff = dateA.diff(dateB).as('days');
    if (dateDiff !== 0) return dateDiff;
    const cardDiff = a.cardName?.localeCompare(b.cardName) || 0;
    if (cardDiff !== 0) return cardDiff;
    const itemDiff = a['item-name']?.localeCompare(b['item-name']) || 0;
    if (itemDiff !== 0) return itemDiff;
    const bidItemDiff = a['bidItem-name']?.localeCompare(b['bidItem-name']) || 0;
    if (bidItemDiff !== 0) return bidItemDiff;
    const segmentDiff = a['segment-name']?.localeCompare(b['segment-name']) || 0;
    if (segmentDiff !== 0) return segmentDiff;
    return 0;
  });
};

/**
 * Preparing work report entries for export
 * 1. We first sort the entries
 * 2. We then batch the entries by the work date (into a crew work sheet)
 * 3. If worksheets is not between bounds [1, 99], we will return an error
 */
export const prepareWorkReportEntriesForExport = (workReportEntries) => {
  const workDateMap = {};
  const sortedEntries = sortEntriesForExport(workReportEntries);

  sortedEntries.forEach((workReportEntry) => {
    if (!workDateMap[workReportEntry.dateWorked]) workDateMap[workReportEntry.dateWorked] = [];
    workDateMap[workReportEntry.dateWorked].push(workReportEntry);
  });

  const keys = Object.keys(workDateMap);
  if (keys.length > 99 || keys.length < 1) {
    return {
      error: true,
      message: 'There must be between 1 and 99 work sheets',
    };
  }

  return {
    error: false,
    data: workDateMap,
  };
};

export const addPaddingToNumber = (number, length, shouldAddPositiveWhitespace = false) => {
  const numberString = Math.abs(number).toString();
  const prefix = number < 0 ? '-' : '';
  if ((numberString.length + prefix.length) >= length) return prefix + numberString.slice(0, length - prefix.length);
  const shouldAddWhitespace = shouldAddPositiveWhitespace && number >= 0;
  const stringLength = shouldAddWhitespace ? numberString.length + 1 : numberString.length;
  const resultingString = prefix + '0'.repeat(length - stringLength - prefix.length) + numberString;
  if (shouldAddWhitespace) {
    return ' '.concat(resultingString);
  }
  return resultingString;
};

const addPaddingToText = (text, length) => {
  if (!text) return ' '.repeat(length);
  if (text.length >= length) return text.slice(0, length);
  return text + ' '.repeat(length - text.length);
};

export const normalizeCommentsForBatchExport = (comments) => {
  if (comments == null || comments === '') return '';
  return String(comments).replace(/\r\n|\r|\n/g, ' ');
};

export const generateExport = ({
  data,
  batchNumber,
  worksheetNumber,
  contractField,
  uomField,
  inspectorField,
  statuses,
}) => {
  const lines = [];
  let contract = '';
  let numberOfWorksheets = 0;
  let totalQuantity = 0;
  let currentWorksheetNumber = worksheetNumber;
  const createdDate = DateTime.local().toFormat('yyyyMMdd:HH:mm');
  const worksheets = [];

  // For each date, we will create a line for each work report entry
  Object.keys(data).forEach((date) => {
    const datum = data[date];
    const worksheet = {
      number: currentWorksheetNumber,
      workDate: date,
      entries: [],
    };

    datum.forEach((workReportEntry, index) => {
      let line = '11';
      const quantity = workReportEntry.quantity || 0;
      contract = addPaddingToText(workReportEntry[contractField], 7);
      line += contract;
      line += batchNumber;
      line += createdDate;
      line += addPaddingToNumber(currentWorksheetNumber || 1, 6);
      line += workReportEntry.dateWorked;
      line += addPaddingToNumber(index + 1, 2); // Line #
      line += addPaddingToNumber(index + 1, 2); // MMS Seq #
      line += addPaddingToText(workReportEntry.cardName, 10); // Work Order Number
      line += addPaddingToText(workReportEntry['item.name'], 2); // Item Name
      line += getStatus(workReportEntry, statuses) === 'Closed' ? 'Y' : 'N'; // WO Item Completed
      line += addPaddingToText(workReportEntry['segment.name'], 8); // Segment Name
      line += addPaddingToNumber(quantity.toFixed(2), 12, true); // Authorized Quantity
      line += addPaddingToText(workReportEntry[uomField], 5); // UoM
      line += addPaddingToText(workReportEntry['bidItem.name'], 4); // Bid Item Name
      line += ' '.repeat(8); // Supp Schedule 1 always blank
      line += ' '.repeat(10); // Supp Schedule 2 always blank
      line += addPaddingToNumber(quantity.toFixed(2), 12, true); // Authorized Quantity
      line += addPaddingToText(workReportEntry[uomField], 5); // UoM
      line += addPaddingToText(workReportEntry[inspectorField], 30); // Inspector
      line += ' '.repeat(6); // Xref Sheet #
      line += addPaddingToText(normalizeCommentsForBatchExport(workReportEntry.comments), 500); // Comments
      lines.push(line);
      totalQuantity += workReportEntry.quantity || 0;
      worksheet.entries.push({
        id: workReportEntry.id,
      });
    });

    numberOfWorksheets++;
    currentWorksheetNumber++;
    worksheets.push(worksheet);
  });

  let summaryLine = '91';
  summaryLine += contract;
  summaryLine += batchNumber;
  summaryLine += createdDate;
  summaryLine += addPaddingToNumber(lines.length, 4);
  summaryLine += addPaddingToNumber(numberOfWorksheets, 4);
  summaryLine += addPaddingToNumber(totalQuantity.toFixed(2), 14, true);

  lines.push(summaryLine);
  return {
    exportText: lines.join('\n'),
    worksheets,
  };
};
